import { Cable, Plus, Search, X } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { api } from '../api';
import { Drawer, Empty, ErrorMessage, Loading, PageHeader, StatusBadge } from '../components';
import { SearchSelect } from '../SearchSelect';
import { usePermissions } from '../permissions';
import type { Integration, IntegrationInstance } from '../types';

export function IntegrationsPage() {
  const can = usePermissions();
  const [items, setItems] = useState<Integration[]>([]);
  const [instances, setInstances] = useState<IntegrationInstance[]>([]);
  const [error, setError] = useState<unknown>();
  const [formError, setFormError] = useState<unknown>();
  const [form, setForm] = useState<'integration' | 'instance'>();
  const [tab, setTab] = useState<'instances' | 'definitions'>('instances');
  const [search, setSearch] = useState('');
  const [environment, setEnvironment] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    void Promise.all([
      api<Integration[]>('/api/integrations', { signal: controller.signal }),
      api<IntegrationInstance[]>('/api/integration-instances', { signal: controller.signal }),
    ])
      .then(([definitions, runtime]) => {
        setItems(definitions);
        setInstances(runtime);
        setError(undefined);
      })
      .catch((caught) => {
        if (!controller.signal.aborted) setError(caught);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [refresh]);
  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    setSaving(true);
    setFormError(undefined);
    const payload =
      form === 'integration'
        ? {
            key: values.get('key'),
            name: values.get('name'),
            type: values.get('type'),
            capabilities: String(values.get('capabilities') ?? '')
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean),
          }
        : {
            integrationId: values.get('integrationId'),
            key: values.get('key'),
            name: values.get('name'),
            environment: values.get('environment'),
            configuration: {
              endpoint: values.get('endpoint'),
              reportingActorId: values.get('reportingActorId'),
            },
            secretRef: values.get('secretRef') || null,
          };
    try {
      await api(form === 'integration' ? '/api/integrations' : '/api/integration-instances', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setForm(undefined);
      setRefresh((value) => value + 1);
    } catch (caught) {
      setFormError(caught);
    } finally {
      setSaving(false);
    }
  }
  const query = search.trim().toLocaleLowerCase('de');
  const filteredInstances = instances.filter(
    (item) =>
      (!environment || item.environment === environment) &&
      `${item.name} ${item.key} ${item.integration.name}`.toLocaleLowerCase('de').includes(query),
  );
  const filteredItems = items.filter((item) =>
    `${item.name} ${item.key}`.toLocaleLowerCase('de').includes(query),
  );
  return (
    <>
      <PageHeader
        title="Integrationen"
        description="Verbundene Anwendungen und ihre getrennten Umgebungen verwalten."
        actions={
          can('core:integrations:write') && (
            <button
              className="button"
              onClick={() => {
                setFormError(undefined);
                setForm(tab === 'instances' ? 'instance' : 'integration');
              }}
              disabled={loading || (tab === 'instances' && !items.length)}
            >
              <Plus size={16} />
              {tab === 'instances' ? 'Instanz anlegen' : 'Integration registrieren'}
            </button>
          )
        }
      />
      <div
        className="mb-5 flex gap-1 border-b border-line"
        role="tablist"
        aria-label="Integrationsverwaltung"
      >
        {(
          [
            ['instances', 'Instanzen'],
            ['definitions', 'Definitionen'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            tabIndex={tab === key ? 0 : -1}
            aria-selected={tab === key}
            onKeyDown={(event) => {
              if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
                event.preventDefault();
                const next =
                  event.key === 'Home'
                    ? 'instances'
                    : event.key === 'End'
                      ? 'definitions'
                      : tab === 'instances'
                        ? 'definitions'
                        : 'instances';
                setTab(next);
                setSearch('');
                setEnvironment('');
                const buttons =
                  event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
                    '[role="tab"]',
                  );
                buttons?.[next === 'instances' ? 0 : 1]?.focus();
              }
            }}
            className={`border-b-2 px-4 py-3 text-sm font-medium ${tab === key ? 'border-accent text-accent' : 'border-transparent text-muted'}`}
            onClick={() => {
              setTab(key);
              setSearch('');
              setEnvironment('');
            }}
          >
            {label}
            <span className="ml-2 rounded-md bg-slate-100 px-1.5 py-0.5 text-xs text-muted">
              {key === 'instances' ? instances.length : items.length}
            </span>
          </button>
        ))}
      </div>
      <div className="filter-bar">
        <label>
          <span className="label">
            {tab === 'instances' ? 'Instanzen suchen' : 'Integrationen suchen'}
          </span>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-3 text-muted" />
            <input
              className="input w-full pl-9"
              placeholder="Name oder Schlüssel…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </label>
        {tab === 'instances' && (
          <label>
            <span className="label">Umgebung</span>
            <SearchSelect
              className="input w-full"
              value={environment}
              onValueChange={setEnvironment}
            >
              <option value="">Alle Umgebungen</option>
              {[...new Set(instances.map((item) => item.environment))].sort().map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </SearchSelect>
          </label>
        )}
        {(search || environment) && (
          <button
            className="button-secondary"
            onClick={() => {
              setSearch('');
              setEnvironment('');
            }}
          >
            <X size={14} />
            Zurücksetzen
          </button>
        )}
      </div>
      <ErrorMessage error={error} />
      {loading ? (
        <div className="panel">
          <Loading>Integrationen werden geladen…</Loading>
        </div>
      ) : error ? (
        <div className="panel">
          <Empty>
            Integrationen konnten nicht geladen werden.
            <button className="button-secondary" onClick={() => setRefresh((value) => value + 1)}>
              Erneut versuchen
            </button>
          </Empty>
        </div>
      ) : tab === 'instances' ? (
        <div className="panel overflow-x-auto">
          {!filteredInstances.length ? (
            <Empty>
              {instances.length
                ? 'Keine Instanzen für diese Filter.'
                : 'Noch keine Instanzen eingerichtet. Registrieren Sie zuerst eine Integration unter Definitionen.'}
            </Empty>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Instanz</th>
                  <th>Anwendung</th>
                  <th>Umgebung</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredInstances.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="font-semibold">{item.name}</div>
                      <div className="mt-1 text-xs text-muted">{item.key}</div>
                    </td>
                    <td>{item.integration.name}</td>
                    <td>
                      <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium">
                        {item.environment}
                      </span>
                    </td>
                    <td>
                      <StatusBadge status={item.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {!filteredItems.length ? (
            <div className="panel col-span-full">
              <Empty>Keine Integrationen gefunden.</Empty>
            </div>
          ) : (
            filteredItems.map((item) => (
              <article className="panel p-5" key={item.id}>
                <div className="mb-4 flex items-center justify-between">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-accent-dark text-accent">
                    <Cable size={20} />
                  </div>
                  <StatusBadge status={item.status} />
                </div>
                <h2 className="font-semibold">{item.name}</h2>
                <p className="mt-1 text-sm text-muted">
                  {item.type === 'INTERNAL' ? 'Interne Anwendung' : 'Externe Anwendung'} ·{' '}
                  {instances.filter((instance) => instance.integrationId === item.id).length}{' '}
                  Instanzen
                </p>
                <details className="technical">
                  <summary>Technische Definition</summary>
                  <p className="mt-3 break-all">Schlüssel: {item.key}</p>
                  <p className="mt-2 break-words">
                    Funktionen: {item.capabilities.join(', ') || 'Keine'}
                  </p>
                </details>
              </article>
            ))
          )}
        </div>
      )}
      {form && (
        <Drawer
          title={form === 'integration' ? 'Integration registrieren' : 'Instanz anlegen'}
          description={
            form === 'integration'
              ? 'Anwendungsdefinition für eine neue Verbindung.'
              : 'Eine Umgebung mit einer bestehenden Anwendung verbinden.'
          }
          onClose={() => {
            if (!saving) setForm(undefined);
          }}
        >
          <ErrorMessage error={formError} />
          <form onSubmit={create} className="space-y-5">
            {form === 'instance' && (
              <label className="block">
                <span className="label">Integration *</span>
                <SearchSelect name="integrationId" className="input w-full" required>
                  <option value="">Bitte auswählen</option>
                  {items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </SearchSelect>
              </label>
            )}
            <label className="block">
              <span className="label">Name *</span>
              <input
                name="name"
                className="input w-full"
                required
                autoFocus
                placeholder={form === 'integration' ? 'Helpdesk' : 'Helpdesk Produktion'}
              />
            </label>
            <label className="block">
              <span className="label">Technischer Schlüssel *</span>
              <input
                name="key"
                className="input w-full"
                required
                placeholder={form === 'integration' ? 'helpdesk' : 'helpdesk-prod'}
              />
            </label>
            {form === 'integration' ? (
              <>
                <label className="block">
                  <span className="label">Typ</span>
                  <SearchSelect name="type" className="input w-full">
                    <option value="INTERNAL">Interne Anwendung</option>
                    <option value="EXTERNAL">Externe Anwendung</option>
                  </SearchSelect>
                </label>
                <label className="block">
                  <span className="label">Funktionen</span>
                  <input
                    name="capabilities"
                    className="input w-full"
                    placeholder="PROVISION, UPDATE"
                  />
                  <span className="mt-1 block text-xs text-muted">
                    Technische Funktionsnamen durch Komma trennen.
                  </span>
                </label>
              </>
            ) : (
              <>
                <label className="block">
                  <span className="label">Umgebung *</span>
                  <input name="environment" className="input w-full" required placeholder="prod" />
                </label>
                <label className="block">
                  <span className="label">API-Endpunkt *</span>
                  <input
                    name="endpoint"
                    type="url"
                    className="input w-full"
                    required
                    placeholder="https://helpdesk.example.de"
                  />
                </label>
                <label className="block">
                  <span className="label">Dienst-Akteur *</span>
                  <input
                    name="reportingActorId"
                    className="input w-full"
                    required
                    placeholder="ticket-service"
                  />
                </label>
                <label className="block">
                  <span className="label">Secret-Referenz</span>
                  <input
                    name="secretRef"
                    className="input w-full"
                    placeholder="Referenz auf verschlüsselte Konfiguration"
                  />
                  <span className="mt-1 block text-xs text-muted">
                    Nur die Referenz, keinen Secret-Wert eintragen.
                  </span>
                </label>
              </>
            )}
            <div className="flex gap-2">
              <button className="button" disabled={saving}>
                {saving ? 'Wird gespeichert…' : 'Speichern'}
              </button>
              <button
                type="button"
                className="button-secondary"
                disabled={saving}
                onClick={() => setForm(undefined)}
              >
                Abbrechen
              </button>
            </div>
          </form>
        </Drawer>
      )}
    </>
  );
}
